import { TranslateService } from '@ngx-translate/core';
import { getStockHoldCategoryLabelKey } from '@app/shared/utils/stock-hold-timeout.util';
import { o2oInternalGetStatusDisplayText } from '../internal/o2o-internal-table-status.util';

/** Exact English notes persisted by order-service Constants.NOTE_* */
const NOTE_I18N_KEY: Record<string, string> = {
  'Edit order base info': 'o2o.change_history.notes.edit_order_base_info',
  'O2O audit decision': 'o2o.change_history.notes.o2o_audit_decision',
  'Channel order cancel': 'o2o.change_history.notes.channel_order_cancel',
  'Manual order cancel': 'o2o.change_history.notes.manual_order_cancel',
  'Channel order update': 'o2o.change_history.notes.channel_order_update',
  'Store fulfillment failed': 'o2o.change_history.notes.store_fulfillment_failed',
  'Logistics status sync': 'o2o.change_history.notes.logistics_status_sync',
  'Logistics order cancel': 'o2o.change_history.notes.logistics_order_cancel',
  'Logistics sync failed': 'o2o.change_history.notes.logistics_sync_failed',
  'O2O payment confirmation': 'o2o.change_history.notes.o2o_payment_confirm',
  'O2O COD payment settlement': 'o2o.change_history.notes.o2o_payment_settle',
  'O2O order cancellation request': 'o2o.change_history.notes.o2o_cancel_request',
  'O2O status transition': 'o2o.change_history.notes.o2o_status_transition',
  'Payment captured': 'o2o.change_history.notes.payment_captured',
  'Stock-hold timeout cancel': 'o2o.change_history.notes.stock_hold_timeout_cancel',
  'WMS outbound delivery retrography':
    'o2o.change_history.notes.wms_outbound_retrography',
  'WMS result record retrography':
    'o2o.change_history.notes.wms_result_retrography',
  'Item line split preemption wait':
    'o2o.change_history.notes.item_line_split',
  'Delivery failed — entered awaiting retry':
    'o2o.change_history.notes.delivery_enter_awaiting_retry',
  'Delivery auto-retry (scheduler)':
    'o2o.change_history.notes.delivery_auto_retry',
  'Delivery manual retry': 'o2o.change_history.notes.delivery_manual_retry',
  'Delivery return stock after failed attempts':
    'o2o.change_history.notes.delivery_return_stock',
};

const STATUS_TRANSITION_PREFIX = 'O2O status transition:';
const STOCK_HOLD_TIMEOUT_REASON_PREFIX = 'Stock hold timeout - type: ';

const FIELD_I18N_KEY: Record<string, string> = {
  orderStatus: 'o2o.change_history.fields.order_status',
  sourceStatus: 'o2o.change_history.fields.source_status',
  cancelReason: 'o2o.change_history.fields.cancel_reason',
  orderRemarks: 'o2o.change_history.fields.order_remarks',
  receivingStatus: 'o2o.change_history.fields.receiving_status',
  fulfillmentStatus: 'o2o.change_history.fields.fulfillment_status',
  cancelStatus: 'o2o.change_history.fields.cancel_status',
  action: 'o2o.change_history.fields.action',
  deliveryAttemptCount: 'o2o.change_history.fields.delivery_attempt_count',
  nextDeliveryRetryAt: 'o2o.change_history.fields.next_delivery_retry_at',
  scheduled: 'o2o.change_history.fields.scheduled',
};

/** Fields never shown in Before/After columns */
const HIDDEN_FIELDS = new Set(['type', 'exceptionReason']);

const STATUS_FIELDS = new Set([
  'orderStatus',
  'fulfillmentStatus',
  'receivingStatus',
  'cancelStatus',
  'sourceStatus',
]);

function tOrFallback(
  translate: TranslateService,
  key: string,
  fallback: string,
): string {
  const label = translate.instant(key);
  return label !== key ? label : fallback;
}

/** dd/MM/yyyy HH:mm:ss (e.g. 08/09/2026 10:42:33) */
export function formatChangeHistoryDateTime(value: unknown): string {
  if (value == null || value === '') {
    return '—';
  }
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}`;
}

export function formatChangeHistoryNote(
  note: string | undefined | null,
  translate: TranslateService,
): string {
  if (!note) {
    return translate.instant('o2o.view.change_history.unnamed');
  }

  if (note.startsWith(STATUS_TRANSITION_PREFIX)) {
    const event = note.slice(STATUS_TRANSITION_PREFIX.length);
    return tOrFallback(
      translate,
      `o2o.change_history.events.${event}`,
      event,
    );
  }

  const key = NOTE_I18N_KEY[note];
  if (key) {
    return tOrFallback(translate, key, note);
  }

  return note;
}

function translateStatusValue(
  field: string,
  value: unknown,
  translate: TranslateService,
): string {
  const code = String(value ?? '');
  if (!code) {
    return '—';
  }

  const columnProp =
    field === 'cancelStatus'
      ? 'orderStatus'
      : field === 'fulfillmentStatus'
        ? 'fulfillmentStatus'
        : field === 'receivingStatus'
          ? 'receivingStatus'
          : 'orderStatus';

  const i18nKey = o2oInternalGetStatusDisplayText(columnProp, code);
  return tOrFallback(translate, i18nKey, code);
}

function translateCancelReason(
  raw: string,
  translate: TranslateService,
): string {
  if (raw.startsWith(STOCK_HOLD_TIMEOUT_REASON_PREFIX)) {
    const category = raw.slice(STOCK_HOLD_TIMEOUT_REASON_PREFIX.length).trim();
    const categoryLabel = tOrFallback(
      translate,
      getStockHoldCategoryLabelKey(category),
      category,
    );
    return translate.instant(
      'o2o.change_history.cancel_reasons.stock_hold_timeout',
      { category: categoryLabel },
    );
  }

  // Catalogue code optionally followed by free-text note: "CODE | note"
  const pipeIdx = raw.indexOf(' | ');
  if (pipeIdx > 0) {
    const code = raw.slice(0, pipeIdx).trim();
    const note = raw.slice(pipeIdx + 3).trim();
    const codeKey = `o2o.change_history.cancel_reasons.codes.${code}`;
    const codeLabel = tOrFallback(translate, codeKey, code);
    return note ? `${codeLabel} — ${note}` : codeLabel;
  }

  const codeKey = `o2o.change_history.cancel_reasons.codes.${raw}`;
  return tOrFallback(translate, codeKey, raw);
}

function formatFieldValue(
  field: string,
  value: unknown,
  translate: TranslateService,
): string {
  if (value == null || value === '') {
    return '—';
  }

  if (field === 'action') {
    const key = `o2o.view.change_history.actions.${value}`;
    return tOrFallback(translate, key, String(value));
  }

  if (field === 'scheduled') {
    return translate.instant(
      value
        ? 'o2o.change_history.values.yes'
        : 'o2o.change_history.values.no',
    );
  }

  if (field === 'nextDeliveryRetryAt') {
    return formatChangeHistoryDateTime(value);
  }

  if (field === 'deliveryAttemptCount') {
    return translate.instant('o2o.view.change_history.attempt', {
      count: value,
    });
  }

  if (field === 'cancelReason') {
    return translateCancelReason(String(value), translate);
  }

  if (STATUS_FIELDS.has(field)) {
    return translateStatusValue(field, value, translate);
  }

  return String(value);
}

export function formatChangeHistoryValue(
  raw: string | undefined | null,
  translate: TranslateService,
): string {
  if (!raw) {
    return '—';
  }

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return raw;
    }

    const parts: string[] = [];
    for (const [field, value] of Object.entries(parsed)) {
      if (HIDDEN_FIELDS.has(field) || value == null || value === '') {
        continue;
      }
      const fieldKey = FIELD_I18N_KEY[field];
      const fieldLabel = fieldKey
        ? tOrFallback(translate, fieldKey, field)
        : field;
      parts.push(`${fieldLabel}: ${formatFieldValue(field, value, translate)}`);
    }

    return parts.length ? parts.join('\n') : '—';
  } catch {
    return raw;
  }
}

export function changeHistoryNoteSeverity(
  note?: string | null,
): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
  if (!note) {
    return 'secondary';
  }
  if (note.includes('return stock') || note.includes('Return stock')) {
    return 'warn';
  }
  if (note.includes('auto-retry') || note.includes('manual retry')) {
    return 'info';
  }
  if (
    note.includes('awaiting retry') ||
    note.includes('failed') ||
    note.includes('Stock-hold timeout')
  ) {
    return 'danger';
  }
  if (note.startsWith(STATUS_TRANSITION_PREFIX)) {
    return 'info';
  }
  return 'secondary';
}
